import React from "react";
import cx from "classnames";
import {Check, X} from "react-feather";
import {createUseStyles} from "react-jss";
import {Typography} from "../common/Typography";

export const getAnswerTypographySize = (content, hasMedia) => {
    const length = content ? content.length : 0;
    if (!hasMedia && length <= 20) {
        return "xl3";
    }
    if (hasMedia || length <= 45) {
        return "xl2";
    }
    return "xl";
};

const useStyles = createUseStyles((theme) => ({
    alternative: {
        display: "flex",
        width: "100%",
        maxWidth: "588px",
        minHeight: hasMedia => hasMedia ? "120px" : "180px",
        borderRadius: "32px",
        cursor: "pointer",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: 24,
        position: "relative",
        boxSizing: "border-box",
        border: "none",
        fontFamily: "inherit",
        textAlign: "left",
        padding: "16px 24px",
        outline: "none",
        "&:hover:not(:disabled)": {
            border: "2px solid white",
            borderRadius: "32px",
        },
        "&:focus-visible": {
            outline: "3px solid #ffffff",
            outlineOffset: "4px",
        },
        "&.selected:not(.revealed)": {
            border: "6px solid white",
            borderRadius: "32px",
        },
        "&.revealed": {
            cursor: "default",
        },
        "&.correct": {
            border: "4px solid #4ade80 !important",
            borderRadius: "32px",
        },
        "&.incorrect": {
            border: "4px solid #f87171 !important",
            borderRadius: "32px",
        },
        "&.dimmed": {
            opacity: 0.65,
        },
        "&.optionA": {
            backgroundColor: theme.colors.optionA,
            color: "white",
        },
        "&.optionB": {
            backgroundColor: theme.colors.optionB,
            color: "white",
        },
        "&.optionC": {
            backgroundColor: theme.colors.optionC,
            color: "white",
        },
        "&.optionD": {
            backgroundColor: theme.colors.optionD,
            color: "white",
        },
        "&.optionE": {
            backgroundColor: theme.colors.optionE,
            color: "white",
        },
        "&.optionF": {
            backgroundColor: theme.colors.optionF,
            color: "white",
        },
        "@media (max-width: 1200px)": {
            width: "100%",
            maxWidth: "100%",
            height: "auto",
        },
    },
    content: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        flex: 1,
        overflowWrap: "break-word",
        wordBreak: "break-word",
    },
    optionText: {
        textTransform: "uppercase",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        minWidth: 40,
    },
    statusIcon: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginLeft: "auto",
        paddingRight: 8,
    },
    srOnly: {
        position: "absolute",
        width: "1px",
        height: "1px",
        padding: 0,
        margin: "-1px",
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        whiteSpace: "nowrap",
        border: 0,
    },
}));

export const Alternative = ({
    answer,
    option,
    onClick,
    selected,
    hasMedia,
    answerRevealed = false
}) => {
    const classes = useStyles({
        hasMedia: hasMedia
    });
    const typographySize = getAnswerTypographySize(answer.content, hasMedia);

    const isCorrect = answerRevealed && answer.correct;
    const isIncorrect = answerRevealed && selected && !answer.correct;
    const isDimmed = answerRevealed && !answer.correct && !selected;

    return (
        <button
            type="button"
            key={option}
            disabled={Boolean(answerRevealed)}
            aria-pressed={Boolean(selected)}
            className={cx(
                classes.alternative,
                `option${option.toUpperCase()}`,
                {
                    selected,
                    revealed: answerRevealed,
                    correct: isCorrect,
                    incorrect: isIncorrect,
                    dimmed: isDimmed,
                }
            )}
            onClick={onClick}
        >
            <Typography
                tag="span"
                size="xl2"
                bold
                className={classes.optionText}
            >
                {option}
            </Typography>
            <Typography
                tag="span"
                bold
                size={typographySize}
                className={classes.content}
            >
                {answer.content}
            </Typography>
            {isCorrect && (
                <span className={classes.statusIcon}>
                    <Check color="#4ade80" size={28} aria-hidden="true" />
                    <span className={classes.srOnly}>Riktig svar</span>
                </span>
            )}
            {isIncorrect && (
                <span className={classes.statusIcon}>
                    <X color="#f87171" size={28} aria-hidden="true" />
                    <span className={classes.srOnly}>Ditt svar (feil)</span>
                </span>
            )}
        </button>
    );
};
